// Four inventory reports on the report kit, each from its own
// Api/Reports/<Report>/GetAllLazyPagedData for the header's branch (handover
// 28 Oct 2026, RP-N):
//   price      - products with cost, retail, wholesale and minimum price
//   stock      - per product and day: stock, sold, closing balance at WAC
//   credit     - credit sales invoices with what is settled and still owed
//   repayment  - receipt lines against those invoices
// Dated reports load by the picked period; the price list has no date, so its
// rows are stamped with today and the page opens on "All time".
import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getProfitReport } from "../../services/ReportApiService";
import { DatatablePagingParameter, Filterclass } from "../../core/json/DatatablePagingParameters";
import { ReportScreen } from "../../components/report";
import { dateOf, num, pct2, sumBy } from "../../components/report/reportFormat";

const priceOf = (r, level) =>
  num((r.sectionPriceDetail || []).find((s) => `${s?.section?.name ?? ""}`.toLowerCase() === level)?.sectionPrice);

export const KINDS = {
  price: {
    report: "ProductPriceReport",
    key: "productPriceDetails",
    title: "Product Price Report",
    subtitle: "Cost and selling prices of the branch's products",
    unit: "products",
    dated: false,
    columns: [
      { k: "name", t: "Name", type: "avatar" },
      { k: "code", t: "BarCode", type: "code" },
      { k: "category", t: "Category" },
      { k: "cost", t: "Cost", type: "money", noSum: true },
      { k: "retail", t: "BasePrice", type: "money", noSum: true },
      { k: "wholesale", t: "Wholesale", type: "money", noSum: true },
      { k: "minimum", t: "Minimum", type: "money", noSum: true },
      { k: "margin", t: "Margin %", noSum: true, cell: (r) => `${pct2(r.margin)}%` },
    ],
    sort: { k: "name", d: 1 },
    norm: (r, i, today) => {
      const p = r.product || {};
      const retail = num(p.basePrice) || priceOf(r, "retail");
      const cost = num(p.cost);
      return {
        key: p.guid || `price-${i}`,
        date: today,
        name: p.name || "—",
        code: p.barcode || "",
        category: p.category?.name || "",
        cost,
        retail,
        wholesale: priceOf(r, "wholesale"),
        minimum: priceOf(r, "minimum"),
        margin: retail ? ((retail - cost) / retail) * 100 : 0,
      };
    },
    kpis: [
      { label: "Products", value: (l) => l.length, format: "int" },
      { label: "Avg margin %", value: (l) => (l.length ? sumBy(l, "margin") / l.length : 0), hero: true },
      { label: "Below cost", value: (l) => l.filter((r) => r.retail && r.retail < r.cost).length, format: "int", invert: true },
    ],
  },
  stock: {
    report: "ProductStockReport",
    key: "productStockReportDetail",
    title: "Stock Product Report",
    subtitle: "Each product's stock per day: stock in hand, sold, and the balance at the end of the day",
    unit: "days",
    dated: true,
    columns: [
      { k: "date", t: "Date", type: "date" },
      { k: "name", t: "Product", type: "avatar" },
      { k: "stock", t: "Stock", type: "int", noSum: true },
      { k: "sold", t: "Sale Quantity", type: "int" },
      { k: "balance", t: "Balance Quantity", type: "int", noSum: true },
      { k: "cost", t: "Cost", type: "money", noSum: true },
      { k: "price", t: "Base Price", type: "money", noSum: true, off: true },
      { k: "value", t: "Balance Value", type: "money", noSum: true },
    ],
    sort: { k: "date", d: -1 },
    norm: (r, i) => ({
      key: `${r.product?.name || ""}-${r.date}-${i}`,
      date: dateOf(r.date),
      name: r.product?.name || "—",
      stock: num(r.stock),
      sold: num(r.saleQuantity),
      balance: num(r.balanceQuantity),
      cost: num(r.costPrice),
      price: num(r.basePrice),
      value: num(r.balanceQuantity) * num(r.costPrice),
    }),
    kpis: [
      { label: "Sold", value: (l) => sumBy(l, "sold"), format: "int", hero: true },
      { label: "Products", value: (l) => new Set(l.map((r) => r.name)).size, format: "int" },
      { label: "Days with movement", value: (l) => new Set(l.map((r) => r.date)).size, format: "int" },
    ],
    chart: { valueKey: "sold", title: "Units sold over time" },
  },
  credit: {
    report: "CustomerCreditReport",
    key: "customerCreditReportDetail",
    title: "Customer Credit",
    subtitle: "Credit sales invoices: what each customer was billed, has paid and still owes",
    unit: "invoices",
    dated: true,
    columns: [
      { k: "date", t: "Date", type: "date" },
      { k: "billNo", t: "Bill No", type: "code" },
      { k: "customer", t: "Customer", type: "avatar" },
      { k: "amount", t: "Amount", type: "money" },
      { k: "settled", t: "Received", type: "money" },
      { k: "balance", t: "Balance", type: "money" },
      { k: "share", t: "Share of balance", type: "share", shareOf: "balance" },
    ],
    sort: { k: "date", d: -1 },
    norm: (r, i) => ({
      key: `${r.billNo || i}`,
      date: dateOf(r.date),
      billNo: r.billNo || "",
      customer: r.customer?.name || "—",
      amount: num(r.amount),
      settled: num(r.settled),
      balance: num(r.balance),
    }),
    kpis: [
      { label: "Credit sales", value: (l) => sumBy(l, "amount") },
      { label: "Received", value: (l) => sumBy(l, "settled") },
      { label: "Still owed", value: (l) => sumBy(l, "balance"), hero: true, invert: true },
      { label: "Customers", value: (l) => new Set(l.map((r) => r.customer)).size, format: "int" },
    ],
    chart: { valueKey: "amount", title: "Credit sales over time" },
  },
  repayment: {
    report: "CustomerCreditRepaymentReport",
    key: "customerCreditRepaymentReportDetail",
    title: "Customer Repayment",
    subtitle: "Money received from customers against their credit invoices",
    unit: "payments",
    dated: true,
    columns: [
      { k: "date", t: "Date", type: "date" },
      { k: "customer", t: "Customer", type: "avatar" },
      { k: "billNo", t: "Bill No", type: "code" },
      { k: "billDate", t: "Bill Date", type: "date" },
      { k: "amount", t: "Amount", type: "money" },
    ],
    sort: { k: "date", d: -1 },
    norm: (r, i) => ({
      key: `${r.billNo || ""}-${r.date}-${i}`,
      date: dateOf(r.date),
      billDate: dateOf(r.billDate),
      billNo: r.billNo || "",
      customer: r.customer?.name || "—",
      amount: num(r.amount),
    }),
    kpis: [
      { label: "Received", value: (l) => sumBy(l, "amount"), hero: true },
      { label: "Payments", value: (l) => l.length, format: "int" },
      { label: "Customers", value: (l) => new Set(l.map((r) => r.customer)).size, format: "int" },
    ],
    chart: { valueKey: "amount", title: "Repayments over time" },
  },
};

const InventoryReport = ({ kind }) => {
  const spec = KINDS[kind];
  const { branch } = useSelector((state) => state.branchlist);
  const [raw, setRaw] = useState([]);
  const [error, setError] = useState("");
  const period = useRef(["", ""]);
  const req = useRef(0);

  const load = useCallback(async () => {
    if (!branch?.guid) return;
    const id = ++req.current;
    const [from, to] = period.current;
    const DatatablePagingParameters = new DatatablePagingParameter();
    DatatablePagingParameters.PageNumber = 1;
    DatatablePagingParameters.PageSize = -1;
    const Filters = new Filterclass();
    Filters.Branch = branch;
    // an open period ("All time") still needs dates on the server
    Filters.FromDate = from || "2000-01-01";
    Filters.ToDate = to || dayjs().format("YYYY-MM-DD");
    try {
      const res = await getProfitReport(spec.report, { DatatablePagingParameters, Filters });
      if (id !== req.current) return;
      const list = res?.data?.[spec.key];
      setRaw(Array.isArray(list) ? list : []);
      setError("");
    } catch (e) {
      if (id !== req.current) return;
      console.error(`${spec.title} failed`, e?.response?.data || e);
      setRaw([]);
      setError(e?.response?.data?.Message || e?.response?.data?.message || "the server did not answer");
    }
  }, [branch, spec]);

  const onPeriodChange = useCallback(
    (from, to) => {
      period.current = [from, to];
      load();
    },
    [load]
  );

  const rows = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    return raw.map((r, i) => spec.norm(r, i, today));
  }, [raw, spec]);

  const tabs = useMemo(
    () => [{ id: "detail", label: spec.title, columns: spec.columns, defaultSort: spec.sort, rowKey: (r) => r.key, drillDown: false }],
    [spec]
  );
  const kpis = useMemo(() => spec.kpis, [spec]);
  const searchMatch = useCallback(
    (r, q) => [r.name, r.code, r.customer, r.billNo, r.category, r.date].some((s) => String(s || "").toLowerCase().includes(q)),
    []
  );

  return (
    <ReportScreen
      title={spec.title}
      subtitle={error ? `Could not load the report: ${error}` : spec.subtitle}
      unit={spec.unit}
      rows={rows}
      tabs={tabs}
      kpis={kpis}
      chart={spec.chart || false}
      searchMatch={searchMatch}
      searchPlaceholder={kind === "price" ? "Name, barcode or category…" : kind === "stock" ? "Product or date…" : "Customer or bill no…"}
      exportName={spec.title}
      reportNames={{ detail: spec.title }}
      onRefresh={load}
      onPeriodChange={onPeriodChange}
      defaultPreset={spec.dated ? "tmonth" : "all"}
    />
  );
};

InventoryReport.propTypes = {
  kind: PropTypes.oneOf(Object.keys(KINDS)).isRequired,
};

export default InventoryReport;
